import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Shield, ArrowLeft } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

const Privacy = () => {
    const { settings } = useSettings();
    const businessName = settings?.business_name || 'BGRemove';

    return (
        <div className="min-h-screen bg-foreground">
            {/* Header */}
            <div className="container mx-auto px-4 py-6">
                <Link to="/">
                    <Button variant="ghost" className="mb-4">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Home
                    </Button>
                </Link>
            </div>

            {/* Content */}
            <div className="container mx-auto px-4 py-12 sm:py-16">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-12">
                        <div className="w-16 h-16 rounded-2xl bg-primary mx-auto flex items-center justify-center mb-4">
                            <Shield className="w-8 h-8 text-primary-foreground" />
                        </div>
                        <h1 className="font-heading font-bold text-4xl sm:text-5xl text-background tracking-tight mb-4">
                            Privacy Policy
                        </h1>
                        <p className="text-background/60">
                            Last updated: January 24, 2026
                        </p>
                    </div>

                    <div className="bg-card rounded-2xl p-8 sm:p-12 shadow-xl space-y-8 text-foreground">
                        <section>
                            <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                {businessName} ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our background removal service. Please read this privacy policy carefully.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">2. Information We Collect</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                We collect information that you provide directly to us:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                                <li><strong>Account Information:</strong> Email address, username, and password when you create an account</li>
                                <li><strong>Payment Information:</strong> Credit card details and billing information (processed securely through Stripe)</li>
                                <li><strong>Images:</strong> Photos you upload to our service for background removal</li>
                                <li><strong>Usage Data:</strong> Information about how you use our service, including upload history and processing statistics</li>
                                <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">3. How We Use Your Information</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                We use the information we collect to:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                                <li>Provide, maintain, and improve our background removal service</li>
                                <li>Process your images and deliver the results to you</li>
                                <li>Process payments and manage your subscription</li>
                                <li>Send you technical notices, updates, security alerts, and support messages</li>
                                <li>Respond to your comments, questions, and customer service requests</li>
                                <li>Monitor and analyze trends, usage, and activities in connection with our service</li>
                                <li>Detect, prevent, and address technical issues and fraudulent activity</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">4. Image Storage and Processing</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                <strong>Important:</strong> Your privacy is our priority. Images you upload are processed in real-time and are <strong>automatically deleted from our servers after processing</strong>. We do not permanently store your images or use them for any purpose other than background removal. Your images are never shared with third parties or used for training AI models.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">5. Information Sharing</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                                <li><strong>Service Providers:</strong> We use third-party services (Stripe for payments, AWS for hosting) that require access to certain information to perform their functions</li>
                                <li><strong>Legal Requirements:</strong> If required by law or to respond to legal process</li>
                                <li><strong>Protection:</strong> To protect the rights, property, and safety of {businessName}, our users, or the public</li>
                                <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, or acquisition</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">6. Data Security</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption of data in transit and at rest, secure password hashing, and regular security audits. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">7. Your Rights</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                You have the right to:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                                <li>Access the personal information we hold about you</li>
                                <li>Request correction of inaccurate information</li>
                                <li>Request deletion of your account and associated data</li>
                                <li>Object to processing of your personal information</li>
                                <li>Request restriction of processing your personal information</li>
                                <li>Data portability - receive your data in a structured, commonly used format</li>
                                <li>Withdraw consent at any time (where processing is based on consent)</li>
                            </ul>
                            <p className="text-muted-foreground leading-relaxed mt-4">
                                To exercise these rights, please contact us at info@backgroundremove.uk
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">8. Cookies and Tracking</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                We use cookies and similar tracking technologies to track activity on our service and hold certain information. Cookies are files with a small amount of data that may include an anonymous unique identifier. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our service.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">9. Children's Privacy</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us so we can delete it.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">10. International Data Transfers</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                Your information may be transferred to and maintained on servers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. By using our service, you consent to the transfer of your information to our facilities and the third parties with whom we share it.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">11. Changes to This Privacy Policy</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4">12. Contact Us</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                If you have any questions about this Privacy Policy, please contact us at{' '}
                                <a href="mailto:info@backgroundremove.uk" className="text-primary hover:underline">
                                    info@backgroundremove.uk
                                </a>
                            </p>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Privacy;
